// ============================================================
// components/ui.js — small shared pieces every screen builds with:
//   Pill, Toggle, Choices (on/off buttons and rows of them), SwitchRow,
//   Field, Control, Select, ErrorText (form fields), StatusRow, Tag,
//   SectionHeading, CopyButton, DoneButton, DownloadButton, useFlash,
//   icons, upload and share buttons, and the error screen.
//
// In this file, in order:
//   Basic pieces
//   Interface icons
//   Upload button
//   Share buttons
//   Error screen
// ============================================================

import React, { useEffect, useRef, useState } from "react";
import { canShare, imageToBase64, isChunkError, report, shareFile, shareOrCopy } from "../lib/browser";

import { useFilePicker } from "../lib/hooks";

/* ============================================================
   Basic pieces
   ============================================================ */

// The small building blocks every screen uses. Keeping them here (instead
// of repeating the markup) means one place to change how they look or
// behave, and one place that gets accessibility right.

export function Pill({ active, outline, className = "", children, ...rest }) {
  const classes = ["btn-pill", outline && "btn-pill--outline", active && "btn-pill--active", className]
    .filter(Boolean).join(" ");
  return <button type="button" className={classes} {...rest}>{children}</button>;
}

export function SectionHeading({ title, subtitle }) {
  return (
    <div className="section-heading">
      <h2 className="section-heading__title">{title}</h2>
      {subtitle && <p className="section-heading__subtitle">{subtitle}</p>}
    </div>
  );
}

/** On/off switch with a visible label beside it, in a panel row. */
export function SwitchRow({ label, note, checked, onChange }) {
  return (
    <div className="aside-panel__row">
      <span>
        {label}
        {note && <span className="contrast-note">{note}</span>}
      </span>
      <button type="button" role="switch" aria-checked={checked} aria-label={label}
              className={"aside-panel__switch" + (checked ? " aside-panel__switch--on" : "")}
              onClick={() => onChange(!checked)} />
    </div>
  );
}

/**
 * A tinted message row: tone is "done" (green), "warning", "error", "working" or "info".
 * Pass `action` for a button on the right; `bare` skips the rows wrapper.
 */
export function StatusRow({ tone = "info", children, action, role, bare = false }) {
  const row = (
    <div className={"aside-panel__row caption-status caption-status--" + tone} role={role || (tone === "error" ? "alert" : undefined)}>
      <span>{children}</span>
      {action}
    </div>
  );
  return bare ? row : <div className="aside-panel__rows">{row}</div>;
}

/** Small pill label: tone "pass", "error", "warning" or none. */
export function Tag({ tone, children }) {
  return <span className={"aside-panel__tag aside-panel__tag--small" + (tone ? " aside-panel__tag--" + tone : "")}>{children}</span>;
}

/**
 * A message that shows for a moment ("Copied"), then clears itself:
 * `const [message, flash] = useFlash(); flash("Copied")`.
 */
export function useFlash(ms = 1800) {
  const [message, setMessage] = useState("");
  const timer = useRef(null);
  useEffect(() => () => clearTimeout(timer.current), []);
  const flash = (text) => {
    setMessage(text);
    clearTimeout(timer.current);
    timer.current = setTimeout(() => setMessage(""), ms);
  };
  return [message, flash];
}

/** Copies text to the clipboard and says so for a moment. */
export function CopyButton({ text, label = "Copy", onFail }) {
  const [copied, flash] = useFlash(1500);
  async function copy() {
    try {
      await navigator.clipboard.writeText(text);
      flash("Copied");
    } catch {
      if (onFail) onFail(); // clipboard blocked (insecure origin or permission)
    }
  }
  return <Pill outline onClick={copy}>{copied || label}</Pill>;
}

/**
 * A button that's on or off: `className` plus `className--on` when on.
 * Pressed (aria-pressed) by default; `radio` makes it one choice of a
 * radiogroup (aria-checked) instead.
 */
export function Toggle({ on, radio, className, children, ...rest }) {
  const state = radio ? { role: "radio", "aria-checked": !!on } : { "aria-pressed": !!on };
  return <button type="button" className={className + (on ? ` ${className}--on` : "")} {...state} {...rest}>{children}</button>;
}

/** A row of Toggles, one per [value, label]; the one equal to `value` is on. */
export function Choices({ options, value, onChange, itemClass, radio, ...rest }) {
  return (
    <div role={radio ? "radiogroup" : undefined} {...rest}>
      {options.map(([v, label]) => <Toggle key={v} radio={radio} className={itemClass} on={v === value} onClick={() => onChange(v)}>{label}</Toggle>)}
    </div>
  );
}

/** A labelled panel field: the label, the control (children), a hint and an error. */
export function Field({ id, label, hint, error, children }) {
  return (
    <div className="aside-panel__field">
      <label className="aside-panel__label" htmlFor={id}>{label}</label>
      {children}
      {hint && <span className="contrast-note">{hint}</span>}
      <ErrorText>{error}</ErrorText>
    </div>
  );
}

/** A small control in a form grid: its label above the input (children). */
export function Control({ label, className = "layer-editor__control", children }) {
  return <label className={className}><span>{label}</span>{children}</label>;
}

/** A drop-down list. `options` are [value, label] pairs; onChange gets the value. */
export function Select({ value, onChange, options, className = "size-form__input layer-editor__input", ...rest }) {
  return (
    <select className={className} value={value} onChange={(e) => onChange(e.target.value)} {...rest}>
      {options.map(([v, label]) => <option key={v} value={v}>{label}</option>)}
    </select>
  );
}

/** An error message, read out when it appears; nothing when there's none. */
export function ErrorText({ children }) {
  return children ? <span className="dl-item__error" role="alert">{children}</span> : null;
}

/** "Done": closes a panel (layer editor, record) and goes back. */
export function DoneButton({ onClick, label = "Done" }) {
  return <Pill outline onClick={onClick}><ChevronIcon size={14} strokeWidth={2} style={{ transform: "rotate(90deg)" }} /> {label}</Pill>;
}

/** A download button with the export icon. */
export function DownloadButton({ solid, children, ...rest }) {
  return <Pill outline={!solid} {...rest}><ExportIcon size={14} strokeWidth={2} /> {children}</Pill>;
}

/* ============================================================
   Interface icons
   ============================================================ */

export function Icon({ size = 20, strokeWidth = 1.75, children, ...rest }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor"
         strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" {...rest}>
      {children}
    </svg>
  );
}

// Every icon is a 24×24 line drawing in the text colour: `icon(shapes)` makes one.
const icon = (shapes, extra) => (p) => <Icon {...p} {...extra}>{shapes}</Icon>;

export const GridIcon = icon(<>
  <rect x="3.5" y="3.5" width="7" height="7" rx="1.6" />
  <rect x="13.5" y="3.5" width="7" height="7" rx="1.6" />
  <rect x="3.5" y="13.5" width="7" height="7" rx="1.6" />
  <rect x="13.5" y="13.5" width="7" height="7" rx="1.6" />
</>);
// A person in a circle: accessibility (the free alt text checker).
export const AccessIcon = icon(<>
  <circle cx="12" cy="12" r="9.5" />
  <circle cx="12" cy="7.2" r="1.5" fill="currentColor" stroke="none" />
  <path d="M7.2 10h9.6M12 10v4.2M12 14.2l-2.6 4.1M12 14.2l2.6 4.1" />
</>);
// A painter's palette: Themes.
export const PaletteIcon = icon(<>
  <path d="M12 3.5a8.5 8.5 0 0 0 0 17c1.3 0 1.9-.8 1.9-1.7 0-1.2-1-1.6-1-2.6 0-.9.7-1.5 1.6-1.5h2.2a3.8 3.8 0 0 0 3.8-3.8c0-4.3-3.8-7.4-8.5-7.4z" />
  <circle cx="7.6" cy="11" r="1.1" /><circle cx="10.2" cy="7.4" r="1.1" /><circle cx="14.6" cy="7.6" r="1.1" />
</>);
export const LayersIcon = icon(<>
  <path d="M12 3.5l8.5 4.7L12 13 3.5 8.2 12 3.5z" />
  <path d="M4.5 12.2L12 16.4l7.5-4.2" />
  <path d="M4.5 15.9L12 20.1l7.5-4.2" />
</>);
export const GearIcon = ({ size = 20 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24">
    <circle cx="12" cy="12" r="3.3" fill="none" stroke="currentColor" strokeWidth="1.75" />
    {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
      <rect key={deg} x="11.1" y="1.6" width="1.8" height="4" rx="0.9" fill="currentColor"
            transform={`rotate(${deg} 12 12)`} />
    ))}
  </svg>
);
export const CloseIcon = icon(<path d="M6 6l12 12M18 6L6 18" />);
export const ChevronIcon = icon(<path d="M6 9l6 6 6-6" />);
export const ExportIcon = icon(<><path d="M12 15V3M8 7l4-4 4 4" /><path d="M4 15v3.5A1.5 1.5 0 0 0 5.5 20h13a1.5 1.5 0 0 0 1.5-1.5V15" /></>);
export const CheckIcon = icon(<path d="M5 12.5l4.5 4.5L19 7" />);
export const UploadIcon = icon(<><path d="M12 15V3M7.5 7.5 12 3l4.5 4.5" /><path d="M4.5 16.5V19a1.5 1.5 0 0 0 1.5 1.5h12a1.5 1.5 0 0 0 1.5-1.5v-2.5" /></>);
export const VideoIcon = icon(<><rect x="3" y="6.5" width="12.5" height="11" rx="2.2" /><path d="M15.5 10.5l5-3v9l-5-3z" /></>);
export const PlayIcon = icon(<path d="M8 5.5v13l10.5-6.5L8 5.5z" />, { fill: "currentColor" });
export const HelpIcon = icon(<>
  <circle cx="12" cy="12" r="8.5" />
  <path d="M9.6 9.4a2.5 2.5 0 0 1 4.8.9c0 1.7-2.4 2.1-2.4 3.6" />
  <path d="M12 16.9v.1" />
</>);
export const ShareIcon = icon(<>
  <path d="M12 3.5v11" />
  <path d="M8 7.5l4-4 4 4" />
  <path d="M5 11.5v7a1.5 1.5 0 0 0 1.5 1.5h11a1.5 1.5 0 0 0 1.5-1.5v-7" />
</>);
export const CalendarIcon = icon(<><rect x="3.5" y="5" width="17" height="15.5" rx="2.2" /><path d="M3.5 10h17M8 3v4M16 3v4" /></>);
export const PlusIcon = icon(<path d="M12 5v14M5 12h14" />);
export const SparkIcon = icon(<><path d="M10 3.5l1.6 4.4a2 2 0 0 0 1.2 1.2l4.4 1.6-4.4 1.6a2 2 0 0 0-1.2 1.2L10 17.9l-1.6-4.4a2 2 0 0 0-1.2-1.2L2.8 10.7l4.4-1.6a2 2 0 0 0 1.2-1.2z" /><path d="M18 14.5v5M15.5 17h5" /></>);

/* ============================================================
   Upload button
   ============================================================ */

/**
 * "Upload" button for images. Converts the file to a resized base64 data
 * URL automatically, then calls onImage({ dataUrl, tone, width, height }).
 * Shows its own busy state and any error. With onSvg, an SVG file is handed
 * over as text instead (kept as a vector).
 */
export function UploadButton({ label = "Upload", busyLabel = "Converting…", onImage, onSvg, maxSize = 2000, keepTransparency = false, disabled }) {
  const file = useFilePicker({
    accept: onSvg ? "image/*,.svg" : "image/*",
    onFiles: async (f) => {
      // An SVG stays a vector where the caller wants one (logos); otherwise it's converted like any image.
      if (onSvg && (f.type === "image/svg+xml" || /\.svg$/i.test(f.name))) await onSvg(await f.text());
      else onImage(await imageToBase64(f, { maxSize, keepTransparency }));
    },
  });
  return (
    <>
      <button type="button" className="btn-pill btn-pill--outline" disabled={file.busy || disabled} onClick={file.pick}>
        <UploadIcon size={14} /> {file.busy ? busyLabel : label}
      </button>
      <input {...file.inputProps} />
      <ErrorText>{file.error}</ErrorText>
    </>
  );
}

/* ============================================================
   Share buttons
   ============================================================ */

/**
 * Shares a link or text with the device's share sheet, or copies it where
 * there's no share sheet. `getData` may be async (e.g. building a link).
 */
export function ShareLinkButton({ getData, label = "Share", copyLabel = "Copy link", className, onDone }) {
  const [busy, setBusy] = useState(false);
  const [message, flash] = useFlash();

  async function run() {
    setBusy(true);
    try {
      const result = await shareOrCopy(await getData());
      if ((result === "shared" || result === "copied") && onDone) onDone(result);
      if (result === "copied") flash("Copied with its post text");
      else if (result === "failed") flash("Couldn't share");
    } catch (err) {
      flash(err.message || "Couldn't share");
    }
    setBusy(false);
  }

  return (
    <Pill outline onClick={run} disabled={busy} className={className} aria-live="polite">
      <ShareIcon size={14} strokeWidth={2} /> {message || (busy ? "Preparing…" : canShare() ? label : copyLabel)}
    </Pill>
  );
}

/**
 * Shares a file (a PNG, a video) straight to other apps. `makeFile` builds
 * it on first tap; if the browser then needs a fresh tap (Safari), the
 * button says so and the second tap shares the ready file instantly.
 * Renders nothing where files can't be shared.
 */
export function ShareFileButton({ makeFile, title, text, supported, cacheKey, onDone, className = "btn-pill btn-pill--outline dl-item__button" }) {
  // A prepared file belongs to one design at one size (cacheKey): when that
  // changes it's simply ignored, so Share never sends an out-of-date design.
  const [prep, setPrep] = useState({ key: undefined, file: null, state: "idle" }); // state: idle | preparing | ready
  const [message, flash] = useFlash();
  const fresh = prep.key === cacheKey;
  const file = fresh ? prep.file : null;
  const state = fresh ? prep.state : "idle";
  const update = (patch) => setPrep((p) => ({ ...(p.key === cacheKey ? p : { file: null, state: "idle" }), key: cacheKey, ...patch }));
  if (!supported) return null;

  async function run() {
    let ready = file;
    if (!ready) {
      update({ state: "preparing" });
      try {
        ready = await makeFile();
        update({ file: ready });
      } catch {
        update({ state: "idle" });
        flash("Couldn't prepare");
        return;
      }
    }
    const result = await shareFile(ready, { title, text });
    if (result === "blocked") { update({ state: "ready" }); return; }
    update({ state: "idle" });
    if (result === "shared" && onDone) onDone(result);
    if (result === "unsupported") flash("Can't share here");
  }

  const label = message || (state === "preparing" ? "Preparing…" : state === "ready" ? "Tap again to share" : "Share");
  return (
    <button type="button" className={className} onClick={run} disabled={state === "preparing"} aria-live="polite">
      <ShareIcon size={14} strokeWidth={2} /> {label}
    </button>
  );
}

/* ============================================================
   Error screen
   ============================================================ */

/**
 * If a screen crashes while drawing, show a calm message with a way out
 * instead of a blank page, and report it. `resetKey` (e.g. the page) clears
 * the error when it changes, so moving to another page recovers.
 */
export class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { error: null };
  }
  static getDerivedStateFromError(error) {
    return { error };
  }
  componentDidCatch(error, info) {
    report(error, { kind: "render", extra: { where: ((info && info.componentStack) || "").trim().split("\n").slice(0, 3).join(" < ") } });
  }
  componentDidUpdate(prev) {
    if (this.state.error && prev.resetKey !== this.props.resetKey) this.setState({ error: null });
  }
  render() {
    if (!this.state.error) return this.props.children;
    return (
      <div className="crash" role="alert">
        <h2 className="crash__title">{isChunkError(this.state.error) ? "A new version is ready" : "Something went wrong here"}</h2>
        <p className="crash__text">
          Your work is saved on this device.{" "}
          {isChunkError(this.state.error) ? "Reload to finish updating the app." : this.props.fullPage ? "Reloading usually fixes it." : "Try another page, or reload."}
        </p>
        <div className="aside-panel__actions">
          <button type="button" className="aside-panel__save u-btn-solid" onClick={() => window.location.reload()}>Reload</button>
          {!this.props.fullPage && <button type="button" className="btn-pill btn-pill--outline" onClick={() => this.setState({ error: null })}>Try again</button>}
        </div>
      </div>
    );
  }
}
